/**
 * i18n bootstrap for the producer cabinet.
 *
 * `@most/i18n` owns the shared instance across the SPAs. Until its export surface is pinned we
 * initialize a local i18next instance with the same policy, so this app is not blocked on it:
 *   • RU is the default AND the fallback for every locale (docs/06 §8: `tk → ru`, `en → ru`,
 *     missing key → RU). RU is the only guaranteed-complete bundle.
 *   • The producer namespace only — this app never loads buyer/admin strings.
 *   • `<html lang>` follows the active locale (a11y, docs/06 §12).
 *
 * The locale is persisted server-side on `users.locale` (PATCH /me), because the backend renders
 * emails and Telegram messages for this user in that language — a purely client-side switch
 * would silently desync the notification language from the UI.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import type { Locale } from '@/shared/contract';
import { ru } from './ru';
import { en } from './en';
import { tk } from './tk';

export const LOCALES: Locale[] = ['ru', 'en', 'tk'];

export const LOCALE_LABELS: Record<Locale, string> = {
  ru: 'Русский',
  en: 'English',
  tk: 'Türkmençe',
};

const STORAGE_KEY = 'most.locale';

export function readStoredLocale(): Locale {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'ru' || v === 'en' || v === 'tk') return v;
  } catch {
    // Private mode / disabled storage — fall through to the default.
  }
  return 'ru';
}

export function persistLocale(locale: Locale): void {
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    /* non-fatal */
  }
}

export function applyHtmlLang(locale: Locale): void {
  document.documentElement.lang = locale;
}

void i18n.use(initReactI18next).init({
  resources: {
    ru: { producer: ru },
    en: { producer: en },
    tk: { producer: tk },
  },
  lng: readStoredLocale(),
  // RU is complete; everything else degrades into it rather than showing a raw key.
  fallbackLng: 'ru',
  defaultNS: 'producer',
  ns: ['producer'],
  interpolation: {
    // React already escapes.
    escapeValue: false,
  },
  returnNull: false,
});

applyHtmlLang(readStoredLocale());

export async function changeLocale(locale: Locale): Promise<void> {
  await i18n.changeLanguage(locale);
  persistLocale(locale);
  applyHtmlLang(locale);
}

export default i18n;
